import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Flex, Tbody, Td, Text, Th, Thead, Tr } from '@bonusly/design-system';

import type { BizyTableNode } from '../../../schemas/bizy-conversations';
import { useBizyEvidenceAnalytics } from './bizy-evidence-analytics-context';
import { BizyTableShell } from './bizy-table-shell';
import { exportFilename } from './export-filename';
import { TruncationCaption } from './truncation-caption';

type TablePayload = BizyTableNode['payload'];
type Column = TablePayload['columns'][number];
type Cell = string | number | null;

interface BizyTableProps {
	title?: string;
	columns: Column[];
	rows: { cells: Cell[] }[];
	/** Full match count when rows were trimmed to a subset; enables "N of M". */
	totalRows?: number;
	/** Ranking direction for the "N of M" caption (top/bottom); absent = neutral. */
	selection?: 'top' | 'bottom';
	/** Column index the agent pre-sorted rows by (descending). Shown as the
	 *  initial sort indicator so the reader knows the default ordering. */
	defaultSortColumn?: number;
	/** Drill: clicking a row sends a scoped follow-up for its first-column value. */
	onDrill?: (label: string) => void;
	/** Expand: re-request the full set when the rows were trimmed. */
	onExpand?: () => void;
}

const NUMERIC_FORMATS = new Set(['number', 'percent', 'currency']);

// The renderer formats raw numbers, but the agent sometimes emits a value
// already formatted as a string ("97%", "$1,200"). Append the unit only for a
// raw number; pass a string through untouched so we never double it ("97%%").
function formatCell(value: Cell | undefined, format: Column['format']): string {
	if (value == null) return '';
	switch (format) {
		case 'percent':
			return typeof value === 'number' ? `${value}%` : String(value);
		case 'currency':
			return typeof value === 'number' ? `$${value.toLocaleString()}` : String(value);
		case 'number':
			return typeof value === 'number' ? value.toLocaleString() : String(value);
		default:
			return String(value);
	}
}

// Parse a possibly pre-formatted numeric cell ("97%", "$1,200", "7.6") to a
// number so numeric columns sort by magnitude even when values arrive as
// formatted strings. NaN when there's no number to read.
function toNumber(value: Cell | undefined): number {
	if (typeof value === 'number') return value;
	if (typeof value === 'string') return Number(value.replace(/[$,%\s]/g, ''));
	return NaN;
}

// Chronological key for date-like text ("March 2026", "2026-03") so a Month
// column sorts by time, not alphabetically. NaN for anything not clearly a
// date — those fall back to a lexical compare.
function toDate(value: Cell | undefined): number {
	const s = String(value ?? '');
	if (!/[A-Za-z]{3,}.*\d{4}|\d{4}-\d{2}/.test(s)) return NaN;
	return Date.parse(s);
}

// Leading numeric value inside a compound text cell ("60 givers / 77 receivers"
// → 60, "69% giving / 99% receiving" → 69) so a text column of metric strings
// sorts by its primary number instead of by leading character. NaN when the
// cell has no number to read.
function firstNumber(value: Cell | undefined): number {
	const match = /-?\d[\d,]*\.?\d*/.exec(String(value ?? ''));
	return match ? Number(match[0].replace(/,/g, '')) : NaN;
}

/**
 * Renders the Data Agent's Table node — typed columns + positional rows — with
 * the renderer owning alignment, number/percent/currency formatting, and
 * client-side column sorting. Reuses BizyTableShell for the styled table + CSV /
 * copy + mobile wrapper. When rows were trimmed to a top-N, a caption discloses
 * "top N of M" rather than letting the truncation pass silently.
 *
 * Sorting reorders the displayed rows (the agent pre-sorts by the primary
 * metric; the reader can re-sort by any column). Numeric columns sort
 * numerically; text columns lexically.
 */
export function BizyTable({ title, columns, rows, totalRows, selection, defaultSortColumn, onDrill, onExpand }: BizyTableProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const { conversationId, messageId } = useBizyEvidenceAnalytics();
	const [sort, setSort] = useState<{ col: number; dir: 'asc' | 'desc' } | null>(
		defaultSortColumn != null && defaultSortColumn >= 0 && defaultSortColumn < columns.length ? { col: defaultSortColumn, dir: 'desc' } : null
	);

	const sortedRows = useMemo(() => {
		// Carry each row's original index so it can be the React key: keying by the
		// sorted position would let React update cells in place instead of moving
		// rows, which can fail to repaint on re-sort. A stable per-row key makes
		// React reorder the actual <tr> elements.
		const indexed = rows.map((row, originalIndex) => ({ row, originalIndex }));
		if (!sort) return indexed;
		const { col, dir } = sort;
		// Sort numerically only when the column is numeric-formatted AND actually
		// holds numbers. The agent sometimes labels a text column (names,
		// descriptions) with a numeric format; without this guard every cell would
		// read as "missing" (NaN) and the column wouldn't sort at all.
		const declaredNumeric = NUMERIC_FORMATS.has(columns[col]?.format ?? '');
		const numeric = declaredNumeric && indexed.some(({ row }) => row.cells[col] != null && !Number.isNaN(toNumber(row.cells[col])));
		const factor = dir === 'asc' ? 1 : -1;
		// A cell is "missing" if it's null, or non-numeric in a numeric column
		// (the agent may emit a placeholder string like "—" or "N/A"). Missing
		// cells always sort last, regardless of direction.
		const isMissing = (v: Cell | undefined) => v == null || (numeric && Number.isNaN(toNumber(v)));
		return [...indexed].sort((entryA, entryB) => {
			const av = entryA.row.cells[col];
			const bv = entryB.row.cells[col];
			const aMissing = isMissing(av);
			const bMissing = isMissing(bv);
			if (aMissing && bMissing) return 0;
			if (aMissing) return 1;
			if (bMissing) return -1;
			let cmp: number;
			if (numeric) {
				cmp = toNumber(av) - toNumber(bv);
			} else {
				// Text columns: date-like values sort chronologically; compound
				// metric strings ("60 givers / 77 receivers") sort by their leading
				// number; everything else sorts lexically.
				const ad = toDate(av);
				const bd = toDate(bv);
				const an = firstNumber(av);
				const bn = firstNumber(bv);
				if (!Number.isNaN(ad) && !Number.isNaN(bd)) {
					cmp = ad - bd;
				} else if (!Number.isNaN(an) && !Number.isNaN(bn)) {
					cmp = an - bn;
				} else {
					cmp = String(av).localeCompare(String(bv));
				}
			}
			return cmp * factor;
		});
	}, [rows, sort, columns]);

	if (columns.length === 0) return null;

	const onSort = (col: number) => {
		setSort((prev) => {
			const next = prev?.col === col ? { col, dir: prev.dir === 'asc' ? ('desc' as const) : ('asc' as const) } : { col, dir: 'desc' as const };
			track(AnalyticsEvent.Bizy.TableSorted, {
				column: columns[col]?.label ?? String(col),
				direction: next.dir,
				conversationId,
				messageId,
				path: window.location.pathname,
			});
			return next;
		});
	};

	return (
		<Flex direction="column" gap={1} mb={4} data-testid="bizy-table">
			{title ? (
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{title}
				</Text>
			) : null}
			<BizyTableShell
				filename={exportFilename(title, 'table')}
				caption={
					totalRows != null && totalRows > rows.length ? (
						<TruncationCaption shown={rows.length} total={totalRows} selection={selection} onExpand={onExpand} />
					) : null
				}
			>
				<Thead>
					<Tr>
						{columns.map((col, i) => {
							const active = sort?.col === i;
							return (
								<Th
									key={i}
									textAlign={col.align}
									cursor="pointer"
									userSelect="none"
									aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
									onClick={() => onSort(i)}
								>
									{col.label}
									{active ? (sort?.dir === 'asc' ? ' ▲' : ' ▼') : ''}
								</Th>
							);
						})}
					</Tr>
				</Thead>
				<Tbody>
					{sortedRows.map(({ row, originalIndex }) => {
						const drillLabel = String(row.cells[0] ?? '');
						return (
							<Tr
								key={originalIndex}
								{...(onDrill && drillLabel
									? {
											onClick: () => onDrill(drillLabel),
											cursor: 'pointer',
											'aria-label': t('drillInto', { label: drillLabel, defaultValue: 'Drill into {{label}}' }),
											_hover: { bg: 'secondary.bgAlpha' },
										}
									: {})}
							>
								{columns.map((col, ci) => (
									<Td key={ci} textAlign={col.align}>
										{formatCell(row.cells[ci], col.format)}
									</Td>
								))}
							</Tr>
						);
					})}
				</Tbody>
			</BizyTableShell>
		</Flex>
	);
}
